import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getUser, logout } from '../utils/auth';
import { asList, getJson, periodLabel } from './helpers';
import PortfolioPage from './pages/PortfolioPage';
import ProjectHomePage from './pages/ProjectHomePage';
import ReportsPage from './pages/ReportsPage';
import ToolListPage from './pages/ToolListPage';
import ClaretProjectToolEmbed from './pages/ClaretProjectToolEmbed';
import ClaretCompanyToolEmbed from './pages/ClaretCompanyToolEmbed';
import SettingsLandingPage from './pages/SettingsLandingPage';
import DirectoryPage from './pages/DirectoryPage';
import InvoicingPage from './pages/InvoicingPage';
import ForecastingPage from './pages/ForecastingPage';
import { ProcoreIntegrationProvider } from './IntegrationActions';
import {
  ApInvoiceDetail,
  ChangeOrderDetail,
  CommitmentChangeOrderDetail,
  CommitmentDetail,
  DirectCostDetail,
  DirectoryDetail,
  OwnerBillingDetail,
} from './pages/RecordDetailPage';
import ProjectContracts from '../components/ProjectContracts';
import ProjectBudgets from '../components/ProjectBudgets';
import LaborRateSchedules from '../components/LaborRateSchedules';
import LaborCosts from '../components/LaborCosts';
import ProjectExpenses from '../components/ProjectExpenses';
import ProjectSettings from '../components/ProjectSettings';
import Tenants from '../components/Tenants';
import Users from '../components/Users';
import IntegrationsPage from './pages/IntegrationsPage';
import GLSettings from '../components/GLSettings';
import WIPSettings from '../components/WIPSettings';
import CostCodes from '../components/CostCodes';
import CostTypes from '../components/CostTypes';
import LaborClasses from '../components/LaborClasses';
import Vendors from '../components/Vendors';
import Customers from '../components/Customers';
import Employees from '../components/Employees';
import ChartOfAccounts from '../components/ChartOfAccounts';
import AccountingPeriods from '../components/AccountingPeriods';
import procoreWordmark from './assets/procore-wordmark.svg';
import './procorePreview.css';

function Icon({ d }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d={d} />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  );
}

function ProcoreAccountingLogo() {
  return (
    <Link className="pc-logo" to="/procore" title="Procore Accounting Layer">
      <span className="pc-logo-stack">
        <img className="pc-logo-mark" src={procoreWordmark} alt="Procore" />
        <span className="pc-logo-sub">Accounting Layer</span>
      </span>
    </Link>
  );
}

function companyEmbed(menu, title, Page) {
  return (
    <PreviewShell>
      <ClaretCompanyToolEmbed menu={menu} title={title}>
        <Page />
      </ClaretCompanyToolEmbed>
    </PreviewShell>
  );
}

function ProjectPicker({ projects, current, onOpen, loadingList }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="pc-picker">
      <button
        type="button"
        className="pc-picker-btn"
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next) onOpen?.();
        }}
      >
        <span>{current ? `${current.project_number} — ${current.project_name}` : 'Select a project'}</span>
        <span>▾</span>
      </button>
      {open && (
        <div className="pc-picker-menu">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/procore');
            }}
          >
            Company Portfolio
            <span className="pc-meta">All projects</span>
          </button>
          {loadingList && projects.length <= 1 ? (
            <button type="button" disabled>
              Loading projects…
            </button>
          ) : null}
          {projects.map((project) => (
            <button
              key={project.vuid}
              type="button"
              onClick={() => {
                setOpen(false);
                navigate(`/procore/projects/${project.vuid}`);
              }}
            >
              {project.project_name}
              <span className="pc-meta">{project.project_number}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PreviewBanner() {
  const [hidden, setHidden] = useState(() => localStorage.getItem('pc-preview-banner') === 'hidden');
  if (hidden) return null;
  return (
    <div className="pc-banner">
      <span>
        Preview only — Claret Financials in a Procore-native layout. Your existing Claret pages are unchanged.{' '}
        <Link to="/">Back to Claret</Link>
      </span>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => {
          localStorage.setItem('pc-preview-banner', 'hidden');
          setHidden(true);
        }}
      >
        ×
      </button>
    </div>
  );
}

function PreviewShell({ children }) {
  const params = useParams();
  const location = useLocation();
  const projectVuid = params.projectVuid;
  const user = getUser();
  const [projects, setProjects] = useState([]);
  const [allProjectsLoaded, setAllProjectsLoaded] = useState(false);
  const [loadingProjectList, setLoadingProjectList] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!projectVuid) {
      setAllProjectsLoaded(false);
      getJson('/api/projects')
        .then((data) => {
          if (cancelled) return;
          setProjects(asList(data));
          setAllProjectsLoaded(true);
        })
        .catch(() => {
          if (!cancelled) setProjects([]);
        });
      return () => {
        cancelled = true;
      };
    }
    getJson(`/api/projects/${projectVuid}`)
      .then((data) => {
        if (cancelled || !data?.vuid) return;
        setProjects((prev) => {
          const rest = prev.filter((project) => project.vuid !== data.vuid);
          return [data, ...rest];
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  const loadAllProjects = () => {
    if (allProjectsLoaded || loadingProjectList) return;
    setLoadingProjectList(true);
    getJson('/api/projects')
      .then((data) => {
        setProjects(asList(data));
        setAllProjectsLoaded(true);
      })
      .catch(() => {})
      .finally(() => setLoadingProjectList(false));
  };
  const current = useMemo(
    () => projects.find((project) => project.vuid === projectVuid) || null,
    [projects, projectVuid]
  );
  const inProject = Boolean(projectVuid);
  const onSettings =
    location.pathname.startsWith('/procore/settings') ||
    location.pathname.startsWith('/procore/setup') ||
    location.pathname.startsWith('/procore/master-data');

  const tools = onSettings
    ? [{ to: '/procore/settings', label: 'Settings' }]
    : inProject
    ? [
        { to: `/procore/projects/${projectVuid}`, label: 'Home', end: true },
        { to: `/procore/projects/${projectVuid}/contracts?project=${projectVuid}`, label: 'Project Contracts' },
        { to: `/procore/projects/${projectVuid}/budget?project=${projectVuid}`, label: 'Budget' },
        { to: `/procore/projects/${projectVuid}/labor-rate-schedule?project=${projectVuid}`, label: 'Labor Rate Schedule' },
        { to: `/procore/projects/${projectVuid}/labor-costs?project=${projectVuid}`, label: 'Labor Costs' },
        { to: `/procore/projects/${projectVuid}/buyout-forecasting?project=${projectVuid}`, label: 'Forecasting' },
        { to: `/procore/projects/${projectVuid}/commitments`, label: 'Commitments' },
        { to: `/procore/projects/${projectVuid}/change-orders`, label: 'Change Orders' },
        { to: `/procore/projects/${projectVuid}/invoicing?project=${projectVuid}`, label: 'Invoicing' },
        { to: `/procore/projects/${projectVuid}/project-expenses?project=${projectVuid}`, label: 'Project Expenses' },
        { to: `/procore/projects/${projectVuid}/direct-costs`, label: 'Direct Costs' },
        { to: `/procore/projects/${projectVuid}/reports`, label: 'Reports' },
      ]
    : [
        { to: '/procore', label: 'Portfolio', end: true },
        { to: '/procore/directory', label: 'Directory' },
        { to: '/procore/reports', label: 'Reports' },
      ];

  return (
    <div className="pc-root">
      <nav className="pc-rail" aria-label="Company tools">
        <NavLink to="/procore" end title="Portfolio" className={({ isActive }) => (isActive && !inProject ? 'pc-active' : '')}>
          <Icon d="M4 10l8-6 8 6v9a1 1 0 01-1 1h-5v-6H10v6H5a1 1 0 01-1-1v-9z" />
        </NavLink>
        <NavLink
          to="/procore/settings"
          title="Settings"
          className={() => (onSettings ? 'pc-active' : '')}
        >
          <GearIcon />
        </NavLink>
        <div className="pc-rail-spacer" />
        <NavLink to="/" title="Exit to Claret">
          <Icon d="M15 8l4 4-4 4M19 12H9M9 5H6a1 1 0 00-1 1v12a1 1 0 001 1h3" />
        </NavLink>
      </nav>
      <div className="pc-main">
        <header className="pc-top">
          <ProcoreAccountingLogo />
          <ProjectPicker
            projects={projects}
            current={current}
            onOpen={inProject ? loadAllProjects : undefined}
            loadingList={loadingProjectList}
          />
          <div className="pc-search">
            <input
              aria-label="Search"
              placeholder={inProject ? 'Search this project' : 'Search portfolio'}
              defaultValue=""
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !inProject) {
                  window.dispatchEvent(new CustomEvent('pc-search', { detail: event.target.value }));
                }
              }}
            />
          </div>
          <div className="pc-top-actions">
            <span>
              {user?.first_name && user?.last_name
                ? `${user.first_name} ${user.last_name}`
                : user?.username || 'User'}
            </span>
            <Link className="pc-exit" to="/">
              Exit preview
            </Link>
            <button type="button" className="pc-exit" onClick={logout}>
              Logout
            </button>
          </div>
        </header>
        <nav className="pc-tools">
          {tools.map((tool) => (
            <NavLink
              key={tool.to}
              to={tool.to}
              end={tool.end}
              className={({ isActive }) => (isActive || (onSettings && tool.to === '/procore/settings') ? 'pc-active' : '')}
            >
              {tool.label}
            </NavLink>
          ))}
        </nav>
        <PreviewBanner />
        {React.isValidElement(children) && children.type === ProjectHomePage
          ? React.cloneElement(children, { initialProject: current })
          : children}
      </div>
    </div>
  );
}

export default function ProcorePreviewApp() {
  return (
    <ProcoreIntegrationProvider>
      <Routes>
      <Route
        path="/"
        element={
          <PreviewShell>
            <PortfolioPage />
          </PreviewShell>
        }
      />
      <Route
        path="directory"
        element={
          <PreviewShell>
            <DirectoryPage />
          </PreviewShell>
        }
      />
      <Route
        path="reports"
        element={
          <PreviewShell>
            <ReportsPage />
          </PreviewShell>
        }
      />
      <Route
        path="reports/:reportId"
        element={
          <PreviewShell>
            <ReportsPage />
          </PreviewShell>
        }
      />
      <Route
        path="directory/:kind/:recordVuid"
        element={
          <PreviewShell>
            <DirectoryDetail />
          </PreviewShell>
        }
      />
      <Route
        path="settings"
        element={
          <PreviewShell>
            <SettingsLandingPage />
          </PreviewShell>
        }
      />
      <Route path="setup/tenants" element={companyEmbed('Setup', 'Tenants', Tenants)} />
      <Route path="setup/users" element={companyEmbed('Setup', 'Users', Users)} />
      <Route
        path="setup/integrations"
        element={
          <PreviewShell>
            <IntegrationsPage />
          </PreviewShell>
        }
      />
      <Route path="setup/gl-settings" element={companyEmbed('Setup', 'GL Settings', GLSettings)} />
      <Route path="setup/wip-settings" element={companyEmbed('Setup', 'WIP Settings', WIPSettings)} />
      <Route path="master-data/cost-codes" element={companyEmbed('Master Data', 'Cost Codes', CostCodes)} />
      <Route path="master-data/cost-types" element={companyEmbed('Master Data', 'Cost Types', CostTypes)} />
      <Route path="master-data/labor-classes" element={companyEmbed('Master Data', 'Labor Classes', LaborClasses)} />
      <Route path="master-data/vendors" element={companyEmbed('Master Data', 'Vendors', Vendors)} />
      <Route path="master-data/customers" element={companyEmbed('Master Data', 'Customers', Customers)} />
      <Route path="master-data/employees" element={companyEmbed('Master Data', 'Employees', Employees)} />
      <Route path="master-data/chart-of-accounts" element={companyEmbed('Master Data', 'Chart of Accounts', ChartOfAccounts)} />
      <Route path="master-data/accounting-periods" element={companyEmbed('Master Data', 'Accounting Periods', AccountingPeriods)} />
      <Route path="projects/:projectVuid/*" element={<ProjectRoutes />} />
    </Routes>
    </ProcoreIntegrationProvider>
  );
}

function ProjectRoutes() {
  const { projectVuid } = useParams();
  return (
    <Routes>
      <Route
        path="/"
        element={
          <PreviewShell>
            <ProjectHomePage key={projectVuid} projectVuid={projectVuid} />
          </PreviewShell>
        }
      />
      <Route
        path="contracts/:recordVuid"
        element={
          <PreviewShell>
            <ClaretProjectToolEmbed
              title="Project Contracts"
              description="Create and edit prime contracts with fee settings, line allocations, and markups — the same workflow as Claret."
              projectVuid={projectVuid}
              recordQueryKey="contract"
              createLabel="Add Contract"
              syncKind="contract"
            >
              <ProjectContracts />
            </ClaretProjectToolEmbed>
          </PreviewShell>
        }
      />
      <Route
        path="commitments/:recordVuid"
        element={
          <PreviewShell>
            <CommitmentDetail />
          </PreviewShell>
        }
      />
      <Route
        path="invoicing/ap/:recordVuid"
        element={
          <PreviewShell>
            <ApInvoiceDetail />
          </PreviewShell>
        }
      />
      <Route
        path="invoicing/ar/:recordVuid"
        element={
          <PreviewShell>
            <OwnerBillingDetail />
          </PreviewShell>
        }
      />
      <Route
        path="change-orders/commitment/:recordVuid/:coNumber"
        element={
          <PreviewShell>
            <CommitmentChangeOrderDetail />
          </PreviewShell>
        }
      />
      <Route
        path="change-orders/:kind/:recordVuid"
        element={
          <PreviewShell>
            <ChangeOrderDetail />
          </PreviewShell>
        }
      />
      <Route
        path="direct-costs/:recordVuid"
        element={
          <PreviewShell>
            <DirectCostDetail />
          </PreviewShell>
        }
      />
      <Route
        path="budget/:recordVuid"
        element={
          <PreviewShell>
            <ClaretProjectToolEmbed
              title="Budget"
              description="Create and edit original budgets and lines — the same workflow as Claret Project Budgets."
              projectVuid={projectVuid}
              recordQueryKey="budget"
              createLabel="Add Budget"
              syncKind="budget"
            >
              <ProjectBudgets />
            </ClaretProjectToolEmbed>
          </PreviewShell>
        }
      />
      <Route
        path="contracts"
        element={
          <PreviewShell>
            <ClaretProjectToolEmbed
              title="Project Contracts"
              description="Create and edit prime contracts with fee settings, line allocations, and markups — the same workflow as Claret."
              projectVuid={projectVuid}
              recordQueryKey="contract"
              createLabel="Add Contract"
              syncKind="contract"
            >
              <ProjectContracts />
            </ClaretProjectToolEmbed>
          </PreviewShell>
        }
      />
      <Route
        path="budget"
        element={
          <PreviewShell>
            <ClaretProjectToolEmbed
              title="Budget"
              description="Create and edit original budgets and lines — the same workflow as Claret Project Budgets."
              projectVuid={projectVuid}
              recordQueryKey="budget"
              createLabel="Add Budget"
              syncKind="budget"
            >
              <ProjectBudgets />
            </ClaretProjectToolEmbed>
          </PreviewShell>
        }
      />
      <Route
        path="labor-rate-schedule"
        element={
          <PreviewShell>
            <ClaretProjectToolEmbed
              title="Labor Rate Schedule"
              description="Enter cost and billing rates by budget cost code and labor class."
              projectVuid={projectVuid}
            >
              <LaborRateSchedules />
            </ClaretProjectToolEmbed>
          </PreviewShell>
        }
      />
      <Route
        path="labor-costs"
        element={
          <PreviewShell>
            <ClaretProjectToolEmbed
              title="Labor Costs"
              description="Create, retrieve, and post payroll labor costs for this project."
              projectVuid={projectVuid}
              syncKind="labor-cost"
            >
              <LaborCosts />
            </ClaretProjectToolEmbed>
          </PreviewShell>
        }
      />
      <Route
        path="project-expenses"
        element={
          <PreviewShell>
            <ClaretProjectToolEmbed
              title="Project Expenses"
              description="Create, retrieve, and post miscellaneous project expenses for this project."
              projectVuid={projectVuid}
            >
              <ProjectExpenses />
            </ClaretProjectToolEmbed>
          </PreviewShell>
        }
      />
      <Route
        path="buyout-forecasting"
        element={
          <PreviewShell>
            <ForecastingPage tab="revenue" />
          </PreviewShell>
        }
      />
      <Route
        path="buyout-forecasting/cost"
        element={
          <PreviewShell>
            <ForecastingPage tab="cost" />
          </PreviewShell>
        }
      />
      <Route
        path="commitments"
        element={
          <PreviewShell>
            <ToolListPage
              title="Commitments"
              projectVuid={projectVuid}
              description="Work order contracts pushed from Procore or retrieved into Claret — origin_id is written back on success."
              syncKind="commitment"
              fetcher={async () => {
                const rows = asList(await getJson('/api/project-commitments', { project_vuid: projectVuid }));
                return rows.map((row) => ({
                  id: row.vuid,
                  href: `/procore/projects/${projectVuid}/commitments/${row.vuid}`,
                  col1: row.commitment_number,
                  col2: row.commitment_name,
                  col3:
                    row.vendor?.vendor_name ||
                    row.vendor?.company_name ||
                    row.vendor?.name ||
                    row.vendor_name ||
                    '',
                  col4: row.commitment_amount ?? (
                    Number(row.original_amount || 0) + Number(row.change_orders_amount || 0)
                  ),
                  colInvoiced: Number(row.invoiced_amount || 0),
                  col5: row.status,
                }));
              }}
              columns={[
                { key: 'col1', label: '#', width: '9.5rem' },
                { key: 'col2', label: 'Title', width: '16rem' },
                { key: 'col3', label: 'Vendor', width: '14rem' },
                { key: 'col4', label: 'Current Amount', money: true, width: '9.5rem' },
                { key: 'colInvoiced', label: 'Invoiced Amount', money: true, width: '9.5rem' },
                { key: 'col5', label: 'Status', status: true, width: '6.5rem' },
              ]}
            />
          </PreviewShell>
        }
      />
      <Route
        path="change-orders"
        element={
          <PreviewShell>
            <ToolListPage
              title="Change Orders"
              projectVuid={projectVuid}
              description="Prime and commitment change orders, including records posted from Procore workflows."
              retrieveKinds={['prime-change-orders', 'internal-change-orders', 'commitment-change-orders']}
              filters={[
                {
                  key: 'coType',
                  label: 'Type',
                  options: [
                    { value: 'all', label: 'All types' },
                    { value: 'prime', label: 'Prime / External' },
                    { value: 'internal', label: 'Internal' },
                    { value: 'commitment', label: 'Commitment' },
                  ],
                },
              ]}
              fetcher={async () => {
                const [external, internal, commitments, periods, ccos] = await Promise.all([
                  getJson('/api/external-change-orders').catch(() => []),
                  getJson('/api/internal-change-orders').catch(() => []),
                  getJson('/api/project-commitments', { project_vuid: projectVuid }).catch(() => []),
                  getJson('/api/accounting-periods').catch(() => []),
                  getJson('/api/commitment-change-orders', { project_vuid: projectVuid }).catch(() => []),
                ]);
                const periodMap = {};
                asList(periods).forEach((period) => {
                  periodMap[period.vuid] = periodLabel(period);
                });
                const extRows = asList(external)
                  .filter((row) => row.project_vuid === projectVuid)
                  .map((row) => ({
                    id: row.vuid,
                    href: `/procore/projects/${projectVuid}/change-orders/external/${row.vuid}`,
                    col1: row.change_order_number,
                    col2: row.description || row.change_order_name,
                    col3: 'Prime / External',
                    colVendor: '',
                    colCommitment: '',
                    col4: row.total_contract_change_amount ?? row.total_budget_change_amount ?? row.total_amount,
                    col5: periodMap[row.accounting_period_vuid] || '—',
                    col6: row.status,
                    coType: 'prime',
                  }));
                const intRows = asList(internal)
                  .filter((row) => row.project_vuid === projectVuid)
                  .map((row) => ({
                    id: row.vuid,
                    href: `/procore/projects/${projectVuid}/change-orders/internal/${row.vuid}`,
                    col1: row.change_order_number,
                    col2: row.description || row.change_order_name,
                    col3: 'Internal',
                    colVendor: '',
                    colCommitment: '',
                    col4: row.total_change_amount,
                    col5: periodMap[row.accounting_period_vuid] || '—',
                    col6: row.status,
                    coType: 'internal',
                  }));
                const commitmentByVuid = {};
                asList(commitments).forEach((row) => {
                  commitmentByVuid[row.vuid] = row;
                });
                const ccoRows = asList(ccos).map((row) => {
                  const commitment = commitmentByVuid[row.commitment_vuid] || {};
                  const number = String(row.change_order_number || '').trim();
                  return {
                    id: row.vuid || `${row.commitment_vuid}-${number}`,
                    href: `/procore/projects/${projectVuid}/change-orders/commitment/${row.commitment_vuid}/${encodeURIComponent(number)}`,
                    col1: number,
                    col2: row.description || commitment.commitment_name || commitment.commitment_number,
                    col3: 'Commitment',
                    colVendor: commitment.vendor?.vendor_name || commitment.vendor_name || '',
                    colCommitment: commitment.commitment_number || '',
                    col4: row.total_amount,
                    col5:
                      periodMap[row.accounting_period_vuid] ||
                      (row.accounting_period ? periodLabel(row.accounting_period) : '—'),
                    col6: row.status,
                    coType: 'commitment',
                  };
                });
                return [...extRows, ...intRows, ...ccoRows];
              }}
              columns={[
                { key: 'col1', label: '#' },
                { key: 'col2', label: 'Title' },
                { key: 'col3', label: 'Type' },
                {
                  key: 'colVendor',
                  label: 'Vendor',
                  showWhen: (values) => !values.coType || values.coType === 'all' || values.coType === 'commitment',
                },
                {
                  key: 'colCommitment',
                  label: 'Commitment #',
                  showWhen: (values) => !values.coType || values.coType === 'all' || values.coType === 'commitment',
                },
                { key: 'col4', label: 'Amount', money: true },
                { key: 'col5', label: 'Period' },
                { key: 'col6', label: 'Status', status: true },
              ]}
            />
          </PreviewShell>
        }
      />
      <Route
        path="invoicing"
        element={
          <PreviewShell>
            <InvoicingPage tab="owner" />
          </PreviewShell>
        }
      />
      <Route
        path="invoicing/subcontractor"
        element={
          <PreviewShell>
            <InvoicingPage tab="ap" />
          </PreviewShell>
        }
      />
      <Route
        path="direct-costs"
        element={
          <PreviewShell>
            <ToolListPage
              title="Direct Costs"
              projectVuid={projectVuid}
              fetcher={async () => {
                const rows = asList(await getJson('/api/project-expenses').catch(() => [])).filter(
                  (row) => row.project_vuid === projectVuid
                );
                return rows.map((row) => ({
                  id: row.vuid,
                  href: `/procore/projects/${projectVuid}/direct-costs/${row.vuid}`,
                  col1: row.expense_number || row.invoice_number || '—',
                  col2: row.description || row.vendor_name,
                  col3: row.expense_date || row.date,
                  col4: row.total_amount || row.amount,
                  col5: row.status,
                  sendKind: 'direct-cost',
                  sendPayload: { source_type: 'project_expense', source_vuid: row.vuid },
                }));
              }}
              columns={[
                { key: 'col1', label: '#' },
                { key: 'col2', label: 'Description' },
                { key: 'col3', label: 'Date' },
                { key: 'col4', label: 'Amount', money: true },
                { key: 'col5', label: 'Status', status: true },
              ]}
            />
          </PreviewShell>
        }
      />
      <Route
        path="reports"
        element={
          <PreviewShell>
            <ReportsPage projectVuid={projectVuid} />
          </PreviewShell>
        }
      />
      <Route
        path="reports/:reportId"
        element={
          <PreviewShell>
            <ReportsPage projectVuid={projectVuid} />
          </PreviewShell>
        }
      />
      <Route
        path="settings"
        element={
          <PreviewShell>
            <ClaretProjectToolEmbed title="Project Settings" projectVuid={projectVuid} hideChrome>
              <ProjectSettings />
            </ClaretProjectToolEmbed>
          </PreviewShell>
        }
      />
    </Routes>
  );
}
